(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  const doc = document;
  const body = doc.body;
  const qs = (selector, root = doc) => root.querySelector(selector);
  const qsa = (selector, root = doc) => [...root.querySelectorAll(selector)];
  const moneyFormat = body?.dataset.currencyFormat || '${{amount}}';

  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[char]);

  const formatMoney = (cents, format = moneyFormat) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const value = Number(cents || 0);
    const withDelimiter = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const key = (format.match(placeholder) || [null, 'amount'])[1];
    const replacements = {
      amount: withDelimiter(value, 2),
      amount_no_decimals: withDelimiter(value, 0),
      amount_with_comma_separator: withDelimiter(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiter(value, 0, '.', ',')
    };
    return format.replace(placeholder, replacements[key] || replacements.amount);
  };

  const toast = (message) => {
    const node = qs('.toast');
    if (!node) return;
    node.textContent = message;
    node.classList.add('is-visible');
    window.clearTimeout(node._timer);
    node._timer = window.setTimeout(() => node.classList.remove('is-visible'), 2400);
  };

  const setDrawer = (drawer, open) => {
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    body.classList.toggle('drawer-open', open);
    if (open) {
      window.setTimeout(() => qs('button, a, input, select, textarea', drawer)?.focus(), 80);
    }
  };

  const closeAllDrawers = () => {
    qsa('.drawer.is-open').forEach((drawer) => setDrawer(drawer, false));
    qs('.mobile-menu')?.classList.remove('is-open');
  };

  const initHeader = () => {
    const header = qs('[data-site-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    qsa('[data-mobile-menu-open]').forEach((button) => button.addEventListener('click', () => {
      const menu = qs('.mobile-menu');
      if (!menu) return;
      menu.classList.add('is-open');
      body.classList.add('drawer-open');
    }));
    qsa('[data-mobile-menu-close], .mobile-menu a').forEach((button) => button.addEventListener('click', () => {
      qs('.mobile-menu')?.classList.remove('is-open');
      body.classList.remove('drawer-open');
    }));
  };

  const initDrawers = () => {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => setDrawer(qs('#CartDrawer'), true)));
    qsa('[data-drawer-close]').forEach((button) => button.addEventListener('click', () => setDrawer(button.closest('.drawer'), false)));
    qsa('[data-search-open]').forEach((button) => button.addEventListener('click', () => setDrawer(qs('#SearchDrawer'), true)));
    qsa('[data-search-close]').forEach((button) => button.addEventListener('click', () => setDrawer(button.closest('.drawer'), false)));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAllDrawers();
    });
  };


  const initSafeSearchForms = (root = doc) => {
    qsa('[data-kgm-safe-search]', root).forEach((form) => {
      if (form.dataset.safeSearchReady) return;
      form.dataset.safeSearchReady = 'true';

      const input = qs('input[type="search"][name="q"]', form);
      if (!input) return;

      const invalidTerms = new Set([
        '',
        'search',
        'search.',
        'search..',
        'search...',
        'search…',
        'search objects, rooms, materials…'
      ]);

      const normalize = (value) => String(value || '').trim().toLowerCase();

      const clearPlaceholderQuery = () => {
        if (invalidTerms.has(normalize(input.value))) input.value = '';
      };

      input.addEventListener('focus', clearPlaceholderQuery);

      form.addEventListener('submit', (event) => {
        const term = normalize(input.value);
        if (invalidTerms.has(term)) {
          event.preventDefault();
          input.value = '';
          input.setCustomValidity('Enter a product, room, or material to search.');
          input.reportValidity();
          input.focus();
          window.setTimeout(() => input.setCustomValidity(''), 1200);
        }
      });
    });
  };

  const initReveal = (root = doc) => {
    const elements = qsa('.reveal:not(.visible)', root);
    if (!elements.length) return;
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px' });
    elements.forEach((element) => observer.observe(element));
  };

  const initHeroParallax = (root = doc) => {
    qsa('[data-hero-parallax]', root).forEach((hero) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const ambient = qs('.luxury-hero__ambient', hero);
      if (!ambient) return;
      hero.addEventListener('pointermove', (event) => {
        const rect = hero.getBoundingClientRect();
        ambient.style.setProperty('--x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        ambient.style.setProperty('--y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      }, { passive: true });
    });
  };

  const initRails = (root = doc) => {
    qsa('[data-product-rail]', root).forEach((rail) => {
      if (rail.dataset.initialized) return;
      rail.dataset.initialized = 'true';
      const section = rail.closest('section');
      qsa('[data-rail-prev]', section).forEach((button) => button.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * 0.75, behavior: 'smooth' })));
      qsa('[data-rail-next]', section).forEach((button) => button.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * 0.75, behavior: 'smooth' })));

      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        isDown = true;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      });
      rail.addEventListener('pointermove', (event) => {
        if (!isDown) return;
        rail.scrollLeft = startScroll - (event.clientX - startX);
      });
      const stop = () => {
        isDown = false;
        rail.classList.remove('is-dragging');
      };
      rail.addEventListener('pointerup', stop);
      rail.addEventListener('pointercancel', stop);
    });
  };

  const updateShippingMeter = (cart) => {
    qsa('[data-shipping-meter]').forEach((meter) => {
      const threshold = Number(meter.dataset.threshold || 25000);
      const remaining = Math.max(threshold - cart.total_price, 0);
      const message = qs('[data-shipping-message]', meter);
      const progress = qs('[data-shipping-progress]', meter);
      if (message) {
        message.textContent = remaining === 0
          ? 'Complimentary delivery unlocked.'
          : `${formatMoney(remaining)} away from complimentary delivery.`;
      }
      if (progress) progress.style.width = `${Math.min((cart.total_price / threshold) * 100, 100)}%`;
    });
  };

  const renderCart = (cart) => {
    qsa('.cart-count').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    const container = qs('[data-cart-items]');
    const footer = qs('[data-cart-footer]');
    const total = qs('[data-cart-total]');
    if (!container) return;
    if (!cart.items.length) {
      container.innerHTML = `
        <div class="empty-state">
          <p>Your cart is currently empty.</p>
          <a class="button button--secondary" href="/collections/all">Explore the collection</a>
        </div>`;
      footer?.classList.add('is-hidden');
    } else {
      container.innerHTML = cart.items.map((item, index) => `
        <article class="cart-line" data-line="${index + 1}">
          <a href="${escapeHTML(item.url)}" class="cart-line__media">
            ${item.image ? `<img src="${escapeHTML(item.image)}&width=240" alt="${escapeHTML(item.product_title)}" loading="lazy">` : ''}
          </a>
          <div class="cart-line__content">
            <h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : ''}
            ${item.selling_plan_allocation?.selling_plan?.name ? `<p>${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
            <div class="cart-line__footer">
              <span>${formatMoney(item.final_line_price)}</span>
              <button class="text-link" type="button" data-cart-remove data-line="${index + 1}">Remove</button>
            </div>
          </div>
        </article>`).join('');
      footer?.classList.remove('is-hidden');
      if (total) total.textContent = formatMoney(cart.total_price);
    }
    updateShippingMeter(cart);
    bindCartRemove();
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  };

  const bindCartRemove = () => {
    qsa('[data-cart-remove]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(button.dataset.line), quantity: 0 })
          });
          if (!response.ok) throw new Error('Unable to update cart.');
          renderCart(await response.json());
        } catch (error) {
          toast(error.message);
          button.disabled = false;
        }
      });
    });
  };

  const addToCart = async (id, quantity = 1, properties = {}, sellingPlan = null) => {
    const item = { id: Number(id), quantity: Number(quantity), properties };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || 'This item could not be added.');
    }
    return response.json();
  };

  const initQuickAdd = (root = doc) => {
    qsa('[data-quick-add]', root).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', async () => {
        const original = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '…';
        try {
          await addToCart(button.dataset.variantId);
          renderCart(await fetchCart());
          if (body.dataset.cartDrawer === 'true') setDrawer(qs('#CartDrawer'), true);
          else toast('Added to cart.');
        } catch (error) {
          toast(error.message);
        } finally {
          button.disabled = false;
          button.innerHTML = original;
        }
      });
    });
  };

  const initProductForms = (root = doc) => {
    qsa('[data-product-form]', root).forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = qs('[type="submit"]', form);
        const formData = new FormData(form);
        submit.disabled = true;
        submit.dataset.label = submit.textContent;
        submit.textContent = 'Adding…';
        try {
          await addToCart(
            formData.get('id'),
            formData.get('quantity') || 1,
            {},
            formData.get('selling_plan') || null
          );
          renderCart(await fetchCart());
          if (body.dataset.cartDrawer === 'true') setDrawer(qs('#CartDrawer'), true);
          else window.location.href = '/cart';
        } catch (error) {
          toast(error.message);
        } finally {
          submit.disabled = false;
          submit.textContent = submit.dataset.label || 'Add to cart';
        }
      });
    });

    qsa('[data-variant-select]', root).forEach((select) => {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const rootNode = select.closest('[data-product-root]');
        const submit = qs('[type="submit"]', rootNode);
        const price = qs('[data-product-price]', rootNode);
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (submit) {
          const available = option.dataset.available === 'true';
          submit.disabled = !available;
          submit.textContent = available ? 'Add to cart' : 'Sold out';
        }
        if (option.dataset.url && history.replaceState) history.replaceState({}, '', option.dataset.url);
      });
    });

    qsa('[data-quantity]', root).forEach((selector) => {
      const input = qs('input', selector);
      qsa('[data-quantity-minus]', selector).forEach((button) => button.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      }));
      qsa('[data-quantity-plus]', selector).forEach((button) => button.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
      }));
    });
  };

  const initPredictiveSearch = () => {
    const input = qs('[data-predictive-search-input]');
    const results = qs('[data-predictive-results]');
    if (!input || !results) return;
    let controller;
    input.addEventListener('input', () => {
      window.clearTimeout(input._timer);
      const term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      input._timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error();
          const data = await response.json();
          const products = data.resources?.results?.products || [];
          results.innerHTML = products.length ? `
            <div class="predictive-grid">
              ${products.map((product) => `
                <a class="predictive-item" href="${escapeHTML(product.url)}">
                  ${product.image ? `<img src="${escapeHTML(product.image)}&width=180" alt="${escapeHTML(product.title)}" loading="lazy">` : ''}
                  <div><h3>${escapeHTML(product.title)}</h3><span>${escapeHTML(product.price)}</span></div>
                </a>`).join('')}
            </div>` : '<p>No objects found. Try another phrase.</p>';
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '<p>Search is temporarily unavailable.</p>';
        }
      }, 220);
    });
  };

  const initTestimonials = (root = doc) => {
    qsa('[data-testimonials]', root).forEach((slider) => {
      const slides = qsa('[data-testimonial-slide]', slider);
      const dots = qsa('[data-testimonial-dot]', slider);
      if (slides.length < 2) return;
      let active = 0;
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === active));
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === active);
          dot.setAttribute('aria-selected', i === active ? 'true' : 'false');
        });
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
      if (slider.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.setInterval(() => show(active + 1), Number(slider.dataset.speed || 6500));
      }
      show(0);
    });
  };

  const initVideo = (root = doc) => {
    qsa('[data-video-toggle]', root).forEach((button) => {
      const section = button.closest('[data-video-section]');
      const video = qs('video', section);
      if (!video) return;
      button.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          button.dataset.state = 'playing';
          button.setAttribute('aria-label', 'Pause video');
        } else {
          video.pause();
          button.dataset.state = 'paused';
          button.setAttribute('aria-label', 'Play video');
        }
        qsa('[data-play-icon]', button).forEach((icon) => icon.hidden = !video.paused);
        qsa('[data-pause-icon]', button).forEach((icon) => icon.hidden = video.paused);
      });
    });
  };

  const initExitOffer = (root = doc) => {
    qsa('[data-exit-offer]', root).forEach((offer) => {
      if (sessionStorage.getItem(`exit-offer-${offer.id}`)) return;
      let opened = false;
      const open = () => {
        if (opened) return;
        opened = true;
        offer.classList.add('is-open');
        offer.setAttribute('aria-hidden', 'false');
        body.classList.add('drawer-open');
      };
      const close = () => {
        offer.classList.remove('is-open');
        offer.setAttribute('aria-hidden', 'true');
        body.classList.remove('drawer-open');
        sessionStorage.setItem(`exit-offer-${offer.id}`, '1');
      };
      qsa('[data-exit-close]', offer).forEach((button) => button.addEventListener('click', close));
      const delay = Number(offer.dataset.delay || 25) * 1000;
      const timer = window.setTimeout(open, delay);
      const mouseLeave = (event) => {
        if (event.clientY <= 0) {
          window.clearTimeout(timer);
          open();
          doc.removeEventListener('mouseleave', mouseLeave);
        }
      };
      doc.addEventListener('mouseleave', mouseLeave);
    });
  };

  const initCursor = () => {
    const glow = qs('.cursor-glow');
    if (!glow || window.matchMedia('(pointer: coarse)').matches) return;
    window.addEventListener('pointermove', (event) => {
      glow.style.left = `${event.clientX}px`;
      glow.style.top = `${event.clientY}px`;
    }, { passive: true });
  };

  const initCollectionSort = () => {
    qsa('[data-sort-by]').forEach((select) => select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    }));
  };

  const initAll = (root = doc) => {
    initReveal(root);
    initHeroParallax(root);
    initRails(root);
    initQuickAdd(root);
    initProductForms(root);
    initTestimonials(root);
    initVideo(root);
    initExitOffer(root);
  };

  doc.addEventListener('DOMContentLoaded', () => {
    initHeader();
  initSafeSearchForms();
    initDrawers();
    initPredictiveSearch();
    initCollectionSort();
    initCursor();
    bindCartRemove();
    fetchCart().then(updateShippingMeter).catch(() => {});
    initAll();
  });

  doc.addEventListener('shopify:section:load', (event) => initAll(event.target));
  doc.addEventListener('shopify:section:unload', () => closeAllDrawers());
})();


// KGM v9: block empty/placeholder searches without redirecting the preview.
document.addEventListener('submit', function kgmStableSearch(event) {
  var form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.getAttribute('role') !== 'search') return;
  var input = form.querySelector('input[type="search"][name="q"]');
  if (!input) return;
  var value = String(input.value || '').trim();
  var normalized = value.toLowerCase();
  if (!value || normalized === 'search' || normalized === 'search...' || normalized === 'search…') {
    event.preventDefault();
    input.value = '';
    input.setCustomValidity('Enter a product, room, or material.');
    input.reportValidity();
    input.focus();
    window.setTimeout(function () { input.setCustomValidity(''); }, 1400);
  }
});

// KGM V10: direct Home navigation safeguard.
document.addEventListener('click', function(e){var a=e.target.closest('[data-home-link]');if(!a)return;e.preventDefault();window.location.assign('/');});
